import posthog, { type PostHogInterface, type Properties } from 'posthog-js';

import { getStaffTitlePersona } from '@/constants/staffTitles';
import { UserModel } from '@/models/UserModel';

import { isPublicReferralAccessRoute } from './isPublicSignerRoute';
import { Loadable } from './Loadable';

const TELEMETRY_SAMPLE_RATE = 0.1;
const SAMPLED_EVENTS = new Set(['$web_vitals', '$pageleave']);
// The invitation token in these URLs is a bearer credential.
const REFERRAL_ACCESS_TOKEN_IN_URL = /\/referrals\/(access|fax)\/[^/?#]+/g;

// Rewrites every string property, so URLs in $current_url, $pathname,
// $referrer, $initial_* and $session_entry_* are all covered.
function redactReferralAccessTokens(properties: Properties | undefined): void {
  if (!properties) {
    return;
  }
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === 'string') {
      properties[key] = value.replace(
        REFERRAL_ACCESS_TOKEN_IN_URL,
        '/referrals/$1/:token'
      );
    }
  }
}

export class PostHog extends Loadable {
  public static identify(user: UserModel): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      const roleProps = {
        user_type: user.user_type?.type ?? null,
        user_type_label: user.getPosition() || null,
        job_title: user.getTitle() || null,
        persona: getStaffTitlePersona(user.getTitle()),
        is_internal: user.email?.toLowerCase().endsWith('@exacare.com') ?? null
      };

      posthog.identify(user.id, {
        email: user.email,
        name: user.fullName,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username ?? undefined,
        ...roleProps
      });

      posthog.register(roleProps);

      if (user.organization_details) {
        posthog.group('organization', user.organization_details.id, {
          name: user.organization_details.name
        });
      }
    } catch (error) {
      console.error('PostHog identify failed:', error);
    }
  }

  public static reset(): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('PostHog reset failed:', error);
    }
  }

  public static captureScreenerAction(
    name: string,
    properties?: Record<string, unknown>
  ): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.capture(name, properties);
    } catch (error) {
      console.error('PostHog captureScreenerAction failed:', error);
    }
  }

  public static setFacility(ctx: {
    scope: 'single' | 'all';
    facilityId: string | null;
    facilityType?: string | null;
  }): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.register({
        facility_id: ctx.scope === 'all' ? 'all' : (ctx.facilityId ?? 'unset'),
        facility_scope: ctx.scope,
        facility_type: ctx.facilityType ?? null
      });
    } catch (error) {
      console.error('PostHog setFacility failed:', error);
    }
  }

  public load(): void {
    if (!PostHog.isEnabled()) {
      return;
    }

    try {
      posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
        api_host: 'https://us.i.posthog.com',
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['a', 'button']
        },
        capture_pageview: true,
        capture_pageleave: true,
        mask_all_text: false,
        mask_all_element_attributes: false,
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
        capture_performance: true,
        before_send: (event) => {
          if (!event) {
            return event;
          }
          // Runs on every route: a token URL can persist as $referrer or
          // $initial_current_url after the recipient navigates away.
          redactReferralAccessTokens(event.properties);
          redactReferralAccessTokens(event.$set);
          redactReferralAccessTokens(event.$set_once);
          if (isPublicReferralAccessRoute()) {
            event.properties.entry_channel =
              window.location.pathname.startsWith('/referrals/fax/')
                ? 'fax'
                : 'unknown';
          }
          if (
            SAMPLED_EVENTS.has(event.event) &&
            Math.random() > TELEMETRY_SAMPLE_RATE
          ) {
            return null;
          }
          return event;
        },
        loaded: (ph: PostHogInterface) => {
          if (import.meta.env.DEV) {
            console.log('PostHog initialized successfully');
            ph.debug();
          }
        },
        on_xhr_error: (failedRequest: XMLHttpRequest) => {
          if (import.meta.env.DEV) {
            console.error('PostHog XHR error:', failedRequest);
          }
        }
      });
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  private static isEnabled(): boolean {
    if (!import.meta.env.VITE_POSTHOG_API_KEY) {
      console.warn('PostHog API key not configured. Skipping initialization.');
      return false;
    }

    if (import.meta.env.MODE === 'test') {
      console.log('PostHog disabled in test environment');
      return false;
    }

    return true;
  }
}
